import {
  AuthAPI,
  AuthConfigResult,
  AuthDirectLoginWithPasswordQuery,
  AuthDirectLoginWithVerificationCodeQuery,
  AuthDirectRequestVerificationCodeQuery,
  UserInfoResult,
  UserModel,
} from '@cibo/core'
import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Location } from 'react-router-dom'
import {
  AuthCheckActionArgs,
  authCheck,
  authCheckSuccess,
  authDirectLoginWithPassword,
  authDirectLoginWithVerificationCode,
  authDirectLogout,
  authDirectRequestVerificationCode,
  authSessionInvalid,
  refreshUserInfo,
  selectAuthDirectLoginError,
  selectAuthDirectLoginLoading,
  selectAuthLoginDevices,
  selectAuthSessionResolved,
  selectAuthSessionValid,
  selectAuthToken,
  selectAuthUserEmail,
  selectAuthUserId,
  selectAuthUserIsLoggedIn,
  selectAuthUserPermissions,
  selectAuthUserPicture,
  selectAuthUsername,
  selectIsEnterprise,
  selectOrgAccountId,
  selectOrgColor,
  selectOrgName,
  selectOrg,
  selectUserHasPassword,
  selectUserPersona,
  selectUserRoles,
} from '../../store'

type Props = {
  onLogout?(): void
}

export const useAuth = (props?: Props) => {
  const dispatch = useDispatch()

  // Session selectors
  const isLoggedIn = useSelector(selectAuthUserIsLoggedIn)
  const authResolved = useSelector(selectAuthSessionResolved)
  const sessionValid = useSelector(selectAuthSessionValid)
  const userEmail = useSelector(selectAuthUserEmail)
  const roles = useSelector(selectUserRoles)
  const username = useSelector(selectAuthUsername)
  const persona = useSelector(selectUserPersona)
  const userId = useSelector(selectAuthUserId)
  const userPermissions = useSelector(selectAuthUserPermissions)
  const userPicture = useSelector(selectAuthUserPicture)
  const userToken = useSelector(selectAuthToken)
  const loginDevices = useSelector(selectAuthLoginDevices)
  const isEnterprise = useSelector(selectIsEnterprise)
  const hasPassword = useSelector(selectUserHasPassword)
  const orgName = useSelector(selectOrgName)
  const orgId = useSelector(selectOrgAccountId)
  const orgColor = useSelector(selectOrgColor)
  const org = useSelector(selectOrg)

  // Direct login selectors
  const directLoginLoading = useSelector(selectAuthDirectLoginLoading)
  const directLoginError = useSelector(selectAuthDirectLoginError)

  useEffect(() => {
    if (!isLoggedIn) {
      props && props.onLogout && props.onLogout()
    }
  }, [isLoggedIn])

  // Actions
  const logout = useCallback(
    (location?: Location) => {
      window.localStorage.setItem('landManager', JSON.stringify({}))
      dispatch(authDirectLogout({ location }))
    },
    [dispatch]
  )

  const loginWithPassword = useCallback(
    (query: AuthDirectLoginWithPasswordQuery, message?: string) => {
      dispatch(authDirectLoginWithPassword(query, message))
    },
    [dispatch]
  )

  const loginWithVerificationCode = useCallback(
    (query: AuthDirectLoginWithVerificationCodeQuery, message?: string) => {
      dispatch(authDirectLoginWithVerificationCode(query, message))
    },
    [dispatch]
  )

  const requestVerificationCode = useCallback(
    (query: AuthDirectRequestVerificationCodeQuery) => {
      dispatch(authDirectRequestVerificationCode(query))
    },
    [dispatch]
  )

  const checkAuth = useCallback(
    (args: AuthCheckActionArgs) => {
      dispatch(authCheck(args))
    },
    [dispatch]
  )

  const invalidateSession = useCallback(() => {
    dispatch(authSessionInvalid())
    dispatch(refreshUserInfo())
  }, [dispatch])

  const refreshUser = useCallback(() => {
    dispatch(refreshUserInfo())
  }, [dispatch])

  const handleAuthSuccess = useCallback(
    (response: AxiosResponse) => {
      dispatch(authCheckSuccess(response))
    },
    [dispatch]
  )

  return {
    // Session state
    authResolved,
    isLoggedIn,
    sessionValid,
    userEmail,
    userId,
    username,
    persona,
    roles,
    userPermissions,
    userPicture,
    userToken,
    loginDevices,
    isEnterprise,
    hasPassword,
    orgName,
    orgId,
    orgColor,
    org,

    // Direct login state
    directLoginLoading,
    directLoginError,

    // Actions
    logout,
    loginWithPassword,
    loginWithVerificationCode,
    requestVerificationCode,
    checkAuth,
    invalidateSession,
    refreshUser,
    handleAuthSuccess,
  }
}

const useConfig = <R>(select: (data: AuthConfigResult) => R) => {
  const { userId } = useAuth()

  return useQuery<AuthConfigResult, Error, R>({
    queryKey: ['auth-config', userId],
    queryFn: async () => {
      const response: AxiosResponse<AuthConfigResult> = await AuthAPI.config()
      return response.data
    },
    select,
    retry: false,
  })
}

export const useChildOrgList = () => useConfig(data => data.owningOrg?.children)

export const useAccessibleOrgs = () => useConfig(data => data.accessibleSubOrgs)

export const useOwningOrg = () => useConfig(data => data.owningOrg)

export const useUserAuthOrg = () => useConfig(data => data)

export const useUser = () => {
  const { userId } = useAuth()
  return useQuery<UserModel | undefined, Error>({
    queryKey: ['userInfo', userId],
    queryFn: async () => {
      try {
        const response: AxiosResponse<UserInfoResult> = await AuthAPI.userInfo()
        return new UserModel(response.data)
      } catch (error) {
        console.error(JSON.stringify(error))
        return undefined
      }
    },
    retry: false,
  })
}
